import {CreateLinkTileCommand} from "../commands/create-link-tile.command";
import {DeleteLinkTileCommand} from "../commands/delete-link-tile.command";
import {ReorderLinkTileCommand} from "../commands/reorder-link-tile.command";
import {UpdateLinkTileCommand} from "../commands/update-link-tile.command";
import type {ICommand} from "../commands/i-command.interface";
import type {LinkTile} from "./entities/link-tile.entity";
import type {LinkTarget} from "./entities/link-targets/link-target";
import {toPayload} from "./link-tile-mapper";

/**
 * Derives the commands a save actually has to send: the target state compared
 * against the baseline the client loaded when it entered edit mode.
 *
 * Why not simply send the whole tile list: moving one tile makes the others
 * reflow, and that is ONE action. Seven commands would be wrong, and a single
 * "here is the new page" would leave the audit trail unable to say what
 * happened.
 *
 * The page uid is a parameter rather than something read off the tiles: a
 * `LinkTile` carries no parent reference, so neither the create payload's
 * `linkviewUid` nor the reorder's envelope uid could be derived from the two
 * lists. Without it every save would abort at parent resolution on the server.
 *
 * The result is ordered creates → updates → deletes → reorder, and the order is
 * load-bearing: the trailing reorder names every tile that exists AFTER the
 * save, so the creates must already have run and the deletes must already be
 * gone for its uid list to resolve inside the one transaction.
 */
export function diffTiles(linkviewUid: string, baseline: LinkTile[], target: LinkTile[]): ICommand[] {
    const baselineById = byId(baseline);
    const targetById = byId(target);

    const creates: ICommand[] = [];
    const updates: ICommand[] = [];
    const deletes: ICommand[] = [];
    let geometryChanged = false;

    for (const tile of target) {
        const previous = baselineById.get(tile.id);

        if (!previous) {
            creates.push(new CreateLinkTileCommand({...toPayload(tile), linkviewUid}));
            continue;
        }

        if (!hasEqualContent(previous, tile)) {
            updates.push(new UpdateLinkTileCommand(contentOf(tile)));
        }

        if (!hasEqualGeometry(previous, tile)) {
            geometryChanged = true;
        }
    }

    for (const tile of baseline) {
        if (!targetById.has(tile.id)) {
            deletes.push(new DeleteLinkTileCommand({uid: tile.id}));
        }
    }

    const commands = [...creates, ...updates, ...deletes];

    if (geometryChanged) {
        commands.push(new ReorderLinkTileCommand({
            linkviewUid,
            order: target.map((tile) => ({uid: tile.id, x: tile.x, y: tile.y, w: tile.w, h: tile.h})),
        }));
    }

    return commands;
}

function byId(tiles: LinkTile[]): Map<string, LinkTile> {
    return new Map(tiles.map((tile) => [tile.id, tile]));
}

/** The update payload is the wire payload without the geometry: a move is not a
 *  content change, and the whole arrangement travels once, in the reorder. */
function contentOf(tile: LinkTile) {
    const {x: _x, y: _y, w: _w, h: _h, ...content} = toPayload(tile);
    return content;
}

/**
 * Compares the fields a user can author. `target` is compared in its SERIALIZED
 * form, and that is not pedantry: `LinkTileHelper.normalizeTarget` returns a new
 * `ExternalLinkTarget` on every call and baseline tiles come freshly
 * deserialized, so an identity or shallow comparison is always unequal and would
 * emit one update per external-target tile on every single save.
 *
 * The optional chaining is required rather than stylistic: a tile without a
 * target is the NORMAL case (a plain label tile), and a bare `toJSON()` would
 * throw inside `saveTiles`, whose contract is never to throw.
 */
function hasEqualContent(a: LinkTile, b: LinkTile): boolean {
    return serializeText(a.text) === serializeText(b.text)
        && a.color === b.color
        && a.icon === b.icon
        && a.imageAssetUid === b.imageAssetUid
        && serializeTarget(a.target) === serializeTarget(b.target);
}

/**
 * `text` is an `RteValue` (`Delta | RichTextDocument`, see its own doc
 * comment), not a primitive — a fresh object every time a tile is
 * built/normalized (same reasoning as `serializeTarget` below), so this
 * compares by VALUE, not by reference.
 *
 * `JSON.stringify(text)` directly (not `JSON.stringify(text.ops)`, unlike
 * before the RTE unification): a `RichTextDocument` has no `.ops` at all —
 * `text.ops` there is `undefined`, and `JSON.stringify(undefined)` returns
 * `undefined` (the value, not the string) for EVERY such tile, making
 * `serializeText(a.text) === serializeText(b.text)` compare `undefined ===
 * undefined` regardless of actual content. `hasEqualContent` would then
 * treat any two tiles with a migrated `text` as having identical content —
 * an edited tile's text change would silently generate no
 * `UpdateLinkTileCommand` at all. Plain `JSON.stringify(text)` serializes
 * either shape correctly, same fix as `toPayload()` in
 * `link-tile-mapper.ts`.
 */
function serializeText(text: LinkTile['text']): string {
    return JSON.stringify(text);
}

function serializeTarget(target: LinkTarget | undefined): string {
    return JSON.stringify(target?.toJSON() ?? null);
}

function hasEqualGeometry(a: LinkTile, b: LinkTile): boolean {
    return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}
